/* jshint esversion: 6 */
import React from 'react';
import InputBehaviorComponent from 'app/components/EditorComponents/InputBehaviorComponent.jsx';
import ErrorModule from 'app/mixins/ErrorModule.jsx';
import FieldError from 'app/components/common/FieldError.jsx';
import UncontrolledTextInput from 'app/components/common/UncontrolledTextInput.jsx';
import HelpButton from 'app/components/common/HelpButton.jsx';
import StyledSelect from 'app/components/common/StyledSelect.jsx';
import SocialInputConfigWidget from 'app/components/EditorComponents/SocialInputConfigWidget.jsx';
import PropTypes from 'prop-types';
import { assetBase } from '@/lib/asset-base.ts';
import { ConfigureStep } from '@/shadcn/components/NewAutomation/ConfigureStep.tsx';
import { fetchInputPlaylists, playlistName } from '@/lib/input-playlists.ts';

class YouTubeInputConfigPanel extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            playlists: null,
            playlistsLoading: true,
            playlistOption: 'ALL',
        }

        this.onPostStyleClick = this.onPostStyleClick.bind(this);
        this.onPlaylistSelect = this.onPlaylistSelect.bind(this);
        this.showPlaylistChooser = this.showPlaylistChooser.bind(this);
        this.onFieldChange = this.onFieldChange.bind(this);

        // post behavior
        this.nativeWithLinkRef = React.createRef();
        this.nativeOnlyRef = React.createRef();
        this.sharedLinkRef = React.createRef();
        this.allVideosRef = React.createRef();
        this.playlistRef = React.createRef();
    }

    componentDidMount() {
        // Truthy, not merely present: Channel stores an empty id, and coming back
        // to this step must not reopen on Playlist with nothing picked.
        if (this.props.input.attrs.playlistID) {
            this.setState({
                playlistOption: 'PLAYLISTS'
            });
        }

        fetchInputPlaylists(this.props.app.config.apiUrl, this.props.input, this.props.app.config.constants)
            .then((playlists) => {
                this.setState({
                    playlists: playlists,
                    playlistsLoading: false
                });
            });
    }

    onPostStyleClick(e) {
        let selectedOption = parseInt(e.target.dataset.option),
            constants = this.props.app.config.constants,
            event = {
                currentTarget: {
                    dataset: {
                        field: `${this.props.statePath}.socialOverrides.postStyle`
                    }
                },
                target: {
                    value: ''
                }
            };

        switch (selectedOption) {
            case constants.SOC_OVRD_STYLE_BOTH:
                this.nativeOnlyRef.current.checked = false;
                this.sharedLinkRef.current.checked = false;
                break;

            case constants.SOC_OVRD_STYLE_MEDIA:
                this.nativeWithLinkRef.current.checked = false;
                this.sharedLinkRef.current.checked = false;
                break;

            case constants.SOC_OVRD_STYLE_LINK:
                this.nativeOnlyRef.current.checked = false;
                this.nativeWithLinkRef.current.checked = false;
                break;

            default:
                break;
        }

        event.target.value = selectedOption;
        this.props.changeHandler(event);

    }

    /*
     * Channel has to CLEAR the stored id, not just hide the select: the id is
     * what the preview and the created input read, so leaving it set kept
     * posting the playlist. The " - <title>" the pick added to the name goes
     * with it.
     */
    choosePlaylistOption(option) {
        let input = this.props.input,
            name = null;

        this.setState({
            playlistOption: option
        });

        if (option !== 'ALL' || !input.attrs || !input.attrs.playlistID) {
            return;
        }

        // Before the id is cleared: the host's change handler writes in place.
        name = playlistName(input.name || '', this.currentPlaylistTitle(), null);

        this.props.changeHandler({
            currentTarget: {dataset: {field: `${this.props.statePath}.name`}},
            target: {value: name}
        });
        this.props.changeHandler({
            currentTarget: {dataset: {field: `${this.props.statePath}.attrs.playlistID`}},
            target: {value: ''}
        });
    }

    /** The title of the playlist the input holds now, when the list has it. */
    currentPlaylistTitle() {
        let id = (this.props.input && this.props.input.attrs) ? this.props.input.attrs.playlistID : '',
            playlist = (this.state.playlists || []).find((item) => { return item.id === id; });

        return playlist ? playlist.title : null;
    }

    /*
     * Keeps the name's " - <playlist>" suffix in step with the pick. Called
     * before the id itself is written, so the current id still names the title
     * being replaced.
     */
    onPlaylistSelect(e) {
        let playlist = (this.state.playlists || []).find((item) => { return item.id === e.target.value; });

        this.props.changeHandler({
            currentTarget: {dataset: {field: `${this.props.statePath}.name`}},
            target: {value: playlistName(this.props.input.name || '', this.currentPlaylistTitle(), playlist ? playlist.title : null)}
        });

    }

    showPlaylistChooser(e) {
        this.choosePlaylistOption(e.target.dataset.option);
    }

    /*
     * The new-design configure step for a YouTube channel input.
     *
     * The three fields are the same three the legacy fieldsets below render, and
     * they go through the same handlers:
     *
     *   Post these videos:  local state ('ALL' / 'PLAYLISTS'), not a backing
     *                       field. It decides whether the playlist select is
     *                       shown, and Channel clears attrs.playlistID
     *                       (choosePlaylistOption).
     *   Playlist            attrs.playlistID, via onPlaylistSelect so the input
     *                       name keeps its " - <playlist>" suffix in step.
     *   What text ...       socialOverrides.postText.
     *
     * The account chip replaces SocialInputConfigWidget, which is identity
     * display only. It reads the input rather than re-fetching the profile, so
     * there is no avatar here — the design's chip draws a mark or a monogram
     * anyway.
     */
    configureSpec() {
        let constants = this.props.app.config.constants,
            input = this.props.input,
            socialOverrides = (input && input.socialOverrides) ? input.socialOverrides : null,
            loading = this.state.playlistsLoading && this.state.playlists === null,
            /*
                Playlists never arrived: either the fetch failed, or there were no
                credentials to fetch with (componentDidMount's else branch, which
                is every input that hasn't been through OAuth yet). The legacy
                fieldsets render nothing in this state, and so does this — offering
                a Playlist option whose select can only ever be empty is worse
                than offering no choice. An empty ARRAY is a different case: a
                channel with no playlists gets the toggle, same as legacy.
            */
            unavailable = !this.state.playlistsLoading && this.state.playlists === null,
            playlistOptions = [],
            fields = [];

        (this.state.playlists || []).map((playlist) => {
            playlistOptions.push({value: playlist.id, label: playlist.title});
        });

        if (!unavailable) {
            fields.push({
                label: 'Post these videos:',
                name: 'playlistOption',
                kind: 'toggle',
                options: [
                    {value: 'ALL', label: 'Channel'},
                    {value: 'PLAYLISTS', label: 'Playlist'}
                ],
                value: this.state.playlistOption,
                disabled: loading,
                help: loading ? 'Loading playlists…' : 'Choose the whole channel, or a single playlist.',
                error: this.fieldError(['CURATION_INPUT_CREATE', 'INPUT_UPDATE', 'INPUT_CREATE'], ['playlistID'])
            });
        }

        if (this.state.playlistOption === 'PLAYLISTS' && !loading && !unavailable) {
            fields.push({
                label: 'Playlist',
                name: 'attrs.playlistID',
                kind: 'select',
                placeholder: 'Select Playlist',
                options: playlistOptions,
                value: (input && input.attrs && input.attrs.playlistID) ? input.attrs.playlistID : undefined
            });
        }

        fields.push({
            label: 'What text should we include?',
            name: 'socialOverrides.postText',
            kind: 'select',
            options: [
                {value: String(constants.SOC_OVRD_TEXT_BOTH), label: 'Title + Description'},
                {value: String(constants.SOC_OVRD_TEXT_CAPTION), label: 'Title'},
                {value: String(constants.SOC_OVRD_NONE), label: 'No Text'}
            ],
            value: String((socialOverrides && socialOverrides.postText !== undefined)
                ? socialOverrides.postText
                : constants.SOC_OVRD_TEXT_BOTH)
        });

        return {
            account: {
                name: (input && input.name) ? input.name : 'YouTube',
                meta: 'YouTube Channel',
                monogram: 'Y',
                network: 'youtube'
            },
            fields: fields
        };
    }

    /* Validation feedback for one backing field, as ConfigureStep renders it. */
    fieldError(keys, fields) {
        return (
            <ErrorModule
                app={this.props.app}
                keys={keys}
                render={(state) => {
                    return (
                        <div>
                            {fields.map((field) => {
                                return (
                                    <FieldError
                                        key={field}
                                        field={field}
                                        errors={state.errors}/>
                                );
                            })}
                        </div>
                    );
                }}/>
        );
    }

    onFieldChange(field, value) {
        let event = {
                currentTarget: {
                    dataset: {
                        field: `${this.props.statePath}.${field.name}`
                    }
                },
                target: {value: value}
            };

        /* Not a backing field itself, but Channel clears the stored id. */
        if (field.name === 'playlistOption') {
            this.choosePlaylistOption(value);
            return;
        }

        if (field.name === 'attrs.playlistID') {
            this.onPlaylistSelect(event);
        }

        /*
         * postText holds a numeric SOC_OVRD_* constant. The select round-trips
         * every option value as a string, so it has to be turned back into a
         * number here or the stored value stops matching the constants
         * everything else compares it against.
         */
        if (field.name === 'socialOverrides.postText') {
            event.target.value = parseInt(value, 10);
        }

        this.props.changeHandler(event);
    }

    render() {
        if (this.props.tile) {
            return (
                <ConfigureStep
                    spec={this.configureSpec()}
                    base={assetBase(this.props.app.config)}
                    onFieldChange={this.onFieldChange} />
            );
        }

        return this.renderLegacy();
    }

    renderLegacy() {
        let name = (this.props.input !== undefined && this.props.input !== null && this.props.input.name) ? this.props.input.name : '',
            socialOverrides = (this.props.input !== undefined && this.props.input !== null && this.props.input.socialOverrides) ? this.props.input.socialOverrides : null,
            selectedPlaylist = (this.props.input !== undefined && this.props.input !== null && this.props.input.attrs.playlistID) ? this.props.input.attrs.playlistID : '',
            constants = this.props.app.config.constants,
            playlistOptions = [];

        if (this.state.playlists !== null) {
            playlistOptions.push({value: '', label: 'Select Playlist'})
            this.state.playlists.map((playlist) => {
                playlistOptions.push({
                    value: playlist.id,
                    label: playlist.title
                });
            });
        }

        let socialOverrideFields = (
            <div>
                <fieldset>
                    <legend>What text should we include?<HelpButton which="fbInputCaption" /></legend>
                    <StyledSelect
                        options={[
                            { value: constants.SOC_OVRD_TEXT_BOTH, label: 'Title + Description' },
                            { value: constants.SOC_OVRD_TEXT_CAPTION, label: 'Title' },
                            { value: constants.SOC_OVRD_NONE, label: 'No Text' }
                        ]}
                        linkedField={this.props.statePath + ".socialOverrides.postText"}
                        value={(socialOverrides && socialOverrides.postText !== undefined) ? socialOverrides.postText : constants.SOC_OVRD_TEXT_BOTH}
                        selectClass="large"
                        onChange={this.props.changeHandler} />
                </fieldset>
            </div>
        );

        if (this.props.onlySocialOverrides !== undefined && this.props.onlySocialOverrides) {
            return socialOverrideFields;
        }

        let socialIdentifier = null
        if (this.props.app.flags.routeViewType === constants.ROUTE_VIEW_SIMPLE) {
            socialIdentifier = (
                <SocialInputConfigWidget
                    psID={constants.POST_SVC_YOUTUBE}
                    {...this.props}/>
            );
        }

        let playlistElement = null;
        if (this.state.playlistsLoading && this.state.playlists === null) {
            playlistElement = (
                <fieldset>
                    <legend>Post these videos:</legend>
                    <ErrorModule
                        app={this.props.app}
                        keys={['CURATION_INPUT_CREATE', 'INPUT_UPDATE', 'INPUT_CREATE']}
                        render={(state) => {
                            return (
                                <FieldError field="playlistID" errors={state.errors} />
                            );
                        }} />
                    <i className="fa fa-spinner fa-spin"/> Loading playlists
                </fieldset>
            )
        } else if (!this.state.playlistsLoading && this.state.playlists !== null) {
            playlistElement = (
                <fieldset>
                    <legend>Post these videos:</legend>
                    <ErrorModule
                        app={this.props.app}
                        keys={['CURATION_INPUT_CREATE', 'INPUT_UPDATE', 'INPUT_CREATE']}
                        render={(state) => {
                            return (
                                <FieldError field="playlistID" errors={state.errors} />
                            );
                        }} />
                    <div className="inline-checkbox-wrapper">
                        <div className="inline-checkbox stacked">
                            <input
                                id="playlist-all"
                                type="radio"
                                ref={this.allVideosRef}
                                checked={(this.state.playlistOption === 'ALL') ? true : false}
                                onChange={this.showPlaylistChooser}
                                data-option={'ALL'} />
                            <label htmlFor="playlist-all">Channel</label>
                        </div>
                        <div className="inline-checkbox stacked">
                            <input
                                id="playlist-playlists"
                                type="radio"
                                ref={this.playlistRef}
                                checked={(this.state.playlistOption === 'PLAYLISTS') ? true : false}
                                onChange={this.showPlaylistChooser}
                                data-option={'PLAYLISTS'} />
                            <label htmlFor="playlist-playlists">Playlist</label>
                        </div>
                    </div>
                    {this.state.playlistOption === 'PLAYLISTS' ? (
                        <div className="playlist-chooser">
                            <StyledSelect
                                options={playlistOptions}
                                linkedField={this.props.statePath + ".attrs.playlistID"}
                                selectClass="large"
                                onChange={(e) => {
                                    this.onPlaylistSelect(e);
                                    this.props.changeHandler(e);
                                }}
                                value={selectedPlaylist}/>
                        </div>
                    ) : null}
                </fieldset>
            );
        }

        return (
            <div className="editor-panel">
                {socialIdentifier}
                {this.props.app.flags.routeViewType === constants.ROUTE_VIEW_ADVANCED ? (
                    <fieldset>
                        <legend>Input Name <span className="required">*</span></legend>
                        <ErrorModule
                            app={this.props.app}
                            keys={['CURATION_INPUT_CREATE', 'INPUT_UPDATE', 'INPUT_CREATE']}
                            render={(state) => {
                                return (
                                    <FieldError field="name" errors={state.errors} />
                                );
                            }} />
                        <UncontrolledTextInput
                            className="text-input medium"
                            placeholder="Add YouTube Channel Name"
                            value={name}
                            onChange={this.props.changeHandler}
                            data-field={this.props.statePath + ".name"} />
                    </fieldset>
                ) : null }
                {playlistElement}
                {socialOverrideFields}
                {this.props.app.flags.routeViewType === constants.ROUTE_VIEW_ADVANCED ? (
                    <InputBehaviorComponent
                        {...this.props}
                        input={this.props.input}
                        changeHandler={this.props.changeHandler}
                        statePath={this.props.statePath} />
                ) : null}
                {this.props.app.flags.routeViewType === constants.ROUTE_VIEW_ADVANCED ? this.props.replayPromo : null}
            </div>
        );
    }

};

YouTubeInputConfigPanel.displayName = "YouTubeInputConfigPanel";

YouTubeInputConfigPanel.propTypes = {
    /** Renders the new-design configure step instead of the legacy fieldsets. */
    tile: PropTypes.bool
};

YouTubeInputConfigPanel.defaultProps = {
    tile: false
};

export default YouTubeInputConfigPanel;
